import classNames from 'classnames'
import React, { useState } from 'react'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useLossPercentagesReadContext } from '@context/LossPercentagesContext'
import { useModal } from '@context/ModalsContext'
import { useProductReadContext, useProductWriteContext } from '@context/ProductContext'

import { Body2, Heading6 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { CloseIcon } from '@ui/icons/Close.icon'

import { isTickmillPartner } from '@utils/companyName.utils'
import { navigateBasedOnProductType } from '@utils/path'
import { wait } from '@utils/wait'

import { TickmillProductType } from '@model/TickmillProductType'

import styles from '@global/templates/FormTemplate.module.scss'

import { Loading } from '@shared/components/Loading/Loading'
import { ActionModal } from '@shared/modals/Action.modal'
import { RiskWarningModal } from '@shared/modals/RiskWarning.modal'

import { ProductRiskWarning } from '../ProductRiskWarning'

interface ProductRegistrationStepsTemplateProps {
  title: string
  subtitle?: string | React.ReactNode
}

export const ProductRegistrationStepsTemplate: React.FC<
  React.PropsWithChildren<ProductRegistrationStepsTemplateProps>
> = (props) => {
  const { title, subtitle, children } = props

  const navigate = useNavigate()

  const { account } = useAccountReadContext()
  const { product } = useProductReadContext()
  const { changeProductContext } = useProductWriteContext()
  const { isDefaultCFDProductType } = useProductReadContext()

  const isCFDProduct = isDefaultCFDProductType()
  const isPartner = isTickmillPartner(account)
  const { percentages } = useLossPercentagesReadContext()
  const lossPercentage = percentages?.percentage
  const [loading, setLoading] = useState(false)
  const riskWarningModal = useModal(RiskWarningModal, {
    selectedProductIds: [product],
    risk: lossPercentage,
  })

  const handleConfirmExit = async () => {
    try {
      setLoading(true)
      await wait(100)
      if (account && account?.tickmillProducts.length > 1) {
        changeProductContext(isCFDProduct ? TickmillProductType.ETD : TickmillProductType.CFD)
      }
      navigateBasedOnProductType(isCFDProduct, navigate, isPartner)
    } finally {
      setLoading(false)
    }
  }

  const exitModal = useModal(ActionModal, { onConfirm: handleConfirmExit })

  return (
    <Loading isLoading={loading} showLoadingIcon>
      <div className={styles.wrapper}>
        <div className={classNames(styles.title, styles.grid)}>
          <div className={styles.closeButtonWrapper}>
            {account && account?.tickmillProducts.length > 1 && (
              <IconButton.Ghost icon={CloseIcon} onClick={() => exitModal.open()} />
            )}
          </div>
          <Heading6 medium>{title}</Heading6>
          <div className={styles.iconWrapper}>
            <ProductRiskWarning onClick={riskWarningModal.open} />
          </div>
        </div>
        {!!subtitle && <Body2 className='has-text-centered'>{subtitle}</Body2>}

        <div className={classNames(styles.content, styles.fullWidth)}>{children}</div>
      </div>
    </Loading>
  )
}
